import React, { useRef, useCallback, useContext } from 'react';
import styled from 'styled-components';

import { Link, LinkContainer } from 'components/graylog/router';
import { IfPermitted } from 'components/common';
import DateTime from 'logic/datetimes/DateTime';
import { Button, DropdownButton, MenuItem, OverlayTrigger, Tooltip } from 'components/graylog';
import { BootstrapModalConfirm } from 'components/bootstrap';
import Routes from 'routing/Routes';
import BootstrapModalForm from 'components/bootstrap/BootstrapModalForm';

import ForwarderForm from './ForwarderForm';
import ForwardersContext from './ForwardersContext';

import { InputProfile, Forwarder, ForwarderPropType, InputProfilePropType } from '../Types';
import { ForwardersActions } from '../stores/ForwardersStore';
import ForwarderStateBadge from '../states/ForwarderStateBadge';
import ForwarderMetricContainer from '../metrics/ForwarderMetricContainer';

const StyledBadge = styled.div`
  & > .label {
    display: block;
    line-height: inherit;
  }
`;

const StyledButton = styled(Button)`
  padding: 0 15px;
`;

type Props = {
  forwarder: Forwarder,
  inputProfile?: InputProfile,
};

const ForwarderEntry = ({ forwarder, inputProfile }: Props) => {
  const { id, title, description, hostname, last_seen_at: lastSeenAt } = forwarder;
  const modal = useRef<BootstrapModalForm>();
  const configModal = useRef<BootstrapModalForm>();

  const { getForwarderContext } = useContext(ForwardersContext);
  const { state: forwarderState } = getForwarderContext(id);

  const _openConfirmation = useCallback(() => modal.current?.open(), [modal]);
  const _closeConfirmation = useCallback(() => modal.current?.close(), [modal]);

  const _openRemoveConfirmation = useCallback(() => configModal.current?.open(), [configModal]);
  const _closeRemoveConfirmation = useCallback(() => configModal.current?.close(), [configModal]);

  const _onDelete = () => {
    ForwardersActions.delete(id).then(() => _closeConfirmation());
  };

  const _onRemoveConfiguration = () => {
    ForwardersActions.removeConfiguration(forwarder).then(() => _closeRemoveConfirmation());
  };

  const maybeClippedDescription = description && description.length > 35 ? `${description.slice(0, 35)}…` : description;

  return (
    <tr>
      <td>
        <Link to={Routes.pluginRoute('SYSTEM_FORWARDERS_FORWARDERID')(id)}>
          {title}
        </Link>
      </td>
      <td>
        <OverlayTrigger overlay={<Tooltip id="lastSeen">Last seen {DateTime.parseFromString(lastSeenAt).toRelativeString()}</Tooltip>}
                        placement="top">
          <StyledBadge>
            <ForwarderStateBadge forwarderId={forwarder.id} />
          </StyledBadge>
        </OverlayTrigger>
      </td>
      <td>
        {description === maybeClippedDescription
          ? description
          : (
            <OverlayTrigger overlay={<Tooltip id="description">{description}</Tooltip>} placement="top">
              <span>{maybeClippedDescription}</span>
            </OverlayTrigger>
          )}
      </td>
      <td>{hostname}</td>
      <td>{inputProfile ? <Link to={Routes.pluginRoute('SYSTEM_INPUT_PROFILES_INPUTPROFILEID')(inputProfile.id)}>{inputProfile.title}</Link> : 'No input profile'}</td>
      <td>
        <ForwarderMetricContainer forwarderId={forwarder.id} />
      </td>
      <td>
        {!inputProfile
          ? (
            <>
              <IfPermitted permissions="forwarders:edit">
                <LinkContainer to={Routes.pluginRoute('SYSTEM_FORWARDERS_CONFIGURE_FORWARDERID')(id)}>
                  <StyledButton bsSize="xsmall" bsStyle="primary">Configure</StyledButton>
                </LinkContainer>
              </IfPermitted>
              {forwarderState && forwarderState !== 'CONNECTED' && (
                <>
                  &nbsp;
                  <IfPermitted permissions="forwarders:delete">
                    <Button bsSize="xsmall" bsStyle="danger" onClick={_openConfirmation}>Delete</Button>
                  </IfPermitted>
                </>
              )}
            </>
          )
          : (
            <>
              <IfPermitted permissions="forwarders:edit">
                <ForwarderForm forwarder={forwarder} buttonBsSize="xsmall" buttonBsStyle="primary" />
              </IfPermitted>
                &nbsp;
              <span key="split-button-default">
                <DropdownButton bsSize="xsmall"
                                id="forwarder-item"
                                title="Choose action"
                                bsStyle="default"
                                pullRight>
                  <LinkContainer to={Routes.pluginRoute('SYSTEM_FORWARDERS_FORWARDERID')(id)}>
                    <MenuItem eventKey="1">View</MenuItem>
                  </LinkContainer>
                  <IfPermitted permissions="forwarders:delete">
                    <MenuItem eventKey="2" onClick={_openRemoveConfirmation}>Remove configuration</MenuItem>
                  </IfPermitted>
                </DropdownButton>{' '}
              </span>
            </>
          )}
        <BootstrapModalConfirm ref={modal}
                               title="Delete forwarder"
                               onConfirm={_onDelete}
                               onCancel={_closeConfirmation}>
          {`Are you sure you want to delete forwarder "${title}"? This will remove all inputs running on this forwarder.`}
        </BootstrapModalConfirm>
        <BootstrapModalConfirm ref={configModal}
                               title="Remove configuration"
                               onConfirm={_onRemoveConfiguration}
                               onCancel={_closeRemoveConfirmation}>
          {`Are you sure you want to remove the configuration of forwarder "${title}"? This will remove all inputs running on this forwarder.`}
        </BootstrapModalConfirm>
      </td>
    </tr>
  );
};

ForwarderEntry.propTypes = {
  forwarder: ForwarderPropType.isRequired,
  inputProfile: InputProfilePropType,
};

ForwarderEntry.defaultProps = {
  inputProfile: undefined,
};

export default ForwarderEntry;
